import React, { useMemo, useState } from "react";
import { hhmmss } from "../../utils/time.js";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton,
  Collapse, Box, Button, Typography
} from "@mui/material";
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  AirplanemodeActive as AirplanemodeActiveIcon,
  FlightLand as FlightLandIcon,
  FlightTakeoff as FlightTakeoffIcon,
} from "@mui/icons-material";
import { lightBlue } from "@mui/material/colors";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import CircularProgress from "@mui/material/CircularProgress";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


/**
 * mode:
 *  - "ARRIVALS": sort by estimatedArrival, show Date column, land icon
 *  - "DEPARTURES": sort by estimatedDeparture, no Date column, takeoff icon
 */
export default function FlightsTable({
  mode = "ARRIVALS",
  jsonFlightData,
  fetchFlights,
  fetchSingle,
  selectedDate,
  setSelectedDate,
}) {
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(false);

  const isArrivals = mode === "ARRIVALS";

  const flightsArr = useMemo(() => {
    const arr = Array.isArray(jsonFlightData)
      ? jsonFlightData
      : Object.values(jsonFlightData || {});
    return arr.filter(Boolean);
  }, [jsonFlightData]);

  const sortedFlights = useMemo(() => {
    const key = isArrivals ? "estimatedArrival" : "estimatedDeparture";
    return flightsArr
      .slice()
      .sort((a, b) => new Date(a?.[key]) - new Date(b?.[key]));
  }, [flightsArr, isArrivals]);

  const handleDatePicked = (newDate) => {
    if (!newDate || !newDate.isValid?.()) return;
    setSelectedDate(newDate);
    handleFetch(newDate);
  };

  const handleRowClick = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  const handleFetch = async (date) => {
    setLoading(true);
    try {
      // some callers might accept (date, updateExisting); harmless to pass 2nd arg
      await fetchFlights(date, true);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevDay = () => {
    const newDate = selectedDate.subtract(1, "day");
    setSelectedDate(newDate);
    handleFetch(newDate);
  };

  const handleNextDay = () => {
    const newDate = selectedDate.add(1, "day");
    setSelectedDate(newDate);
    handleFetch(newDate);
  };

  const FlightIcon = isArrivals ? FlightLandIcon : FlightTakeoffIcon;

  const pick = (o, ...keys) => {
    for (const k of keys) {
      if (o && o[k] !== undefined && o[k] !== null && o[k] !== "") return o[k];
    }
    return "";
  };

  const splitName = (full) => {
    const s = String(full || "").trim();
    if (!s) return { first: "", last: "" };
    const parts = s.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts[0], last: parts.slice(1).join(" ") };
  };

  const getPassengerRow = (sd, idx) => {
    const personType = String(pick(sd, "personType", "person_type") || "STUDENT").toUpperCase();
    const isAdult = personType === "ADULT";

    const id =
      pick(sd, "adultId", "adult_id") ||
      pick(sd, "studentId", "student_id") ||
      pick(sd, "id") ||
      `${idx}`;

    const firstName = pick(sd, "studentName", "student_name") || splitName(pick(sd, "name")).first;
    const surname = pick(sd, "studentSurname", "student_surname") || splitName(pick(sd, "name")).last;

    const schoolOrCampus =
      pick(sd, "attendingSchool", "attending_school") ||
      pick(sd, "campus");

    const umVal = isAdult ? "" : ((sd.unaccompaniedMinor ?? sd.um) ? "Yes" : "No");

    const arrivalDate = pick(sd, "arrivalDate", "arrival_date");
    const departureDate = pick(sd, "departureDate", "departure_date");

    return { personType, id, firstName, surname, schoolOrCampus, umVal, arrivalDate, departureDate };
  };

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
        <IconButton onClick={handlePrevDay} aria-label="Previous Day">
          <NavigateBefore />
        </IconButton>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={selectedDate}
            onChange={handleDatePicked}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 170 } },
            }}
          />
        </LocalizationProvider>

        <IconButton onClick={handleNextDay} aria-label="Next Day">
          <NavigateNext />
        </IconButton>

        <Button
          onClick={() => handleFetch(selectedDate)}
          variant="contained"
          startIcon={<AirplanemodeActiveIcon />}
        >
          Refresh All Flights
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ margin: "0 auto", maxWidth: 1900 }}>
          <Table sx={{ minWidth: 800, color: lightBlue[200], margin: "0 auto" }} size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: lightBlue[100], border: 1.5 }}>
                <TableCell sx={{ border: 1.5 }}>Expand</TableCell>
                <TableCell sx={{ border: 1.5 }} align="center">Flight #</TableCell>

                <TableCell sx={{ border: 1.5 }} align="center">Date</TableCell>
                

                <TableCell sx={{ border: 1.5 }} align="center">Status</TableCell>

                {isArrivals ? (
                  <>
                    <TableCell sx={{ border: 1.5 }} align="center">Dep Airport</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Arr Airport</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Est Dep</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Sched Arr</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ border: 1.5 }} align="center">Dep Airport</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Arr Airport</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Est Dep</TableCell>
                    <TableCell sx={{ border: 1.5 }} align="center">Sched Arr</TableCell>
                  </>
                )}

                <TableCell sx={{ border: 1.5 }} align="center"># Passengers</TableCell>
                <TableCell sx={{ border: 1.5 }} align="center">Last Upd</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedFlights.map((fn) => {
                const rowKey = `${fn.flightNumber}|${fn.flightDate}`;

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow>
                      <TableCell sx={{ border: 1.5 }}>
                        <IconButton size="small" onClick={() => handleRowClick(rowKey)}>
                          {open[rowKey] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ border: 1.5 }} align="center">
                        <Button
                          variant="contained"
                          onClick={() => fetchSingle(fn.flightNumber, fn.flightDate)}
                          startIcon={<FlightIcon />}
                          size="small"
                        >
                          {fn.flightNumber}
                        </Button>
                      </TableCell>

                      
                      <TableCell sx={{ border: 1.5 }} align="center">{fn.flightDate}</TableCell>
                      

                      <TableCell sx={{ border: 1.5 }} align="center">{fn.flightStatus}</TableCell>

                      {isArrivals ? (
                        <>
                          <TableCell sx={{ border: 1.5 }} align="center">{fn.departureAirport}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{fn.arrivalAirport}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{hhmmss(fn.estimatedDeparture)}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{hhmmss(fn.scheduledArrival)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ border: 1.5 }} align="center">{fn.departureAirport}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{fn.arrivalAirport}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{hhmmss(fn.scheduledArrival)}</TableCell>
                          <TableCell sx={{ border: 1.5 }} align="center">{hhmmss(fn.estimatedDeparture)}</TableCell>
                        </>
                      )}

                      <TableCell sx={{ border: 1.5 }} align="center">{fn.totalStudentsOnFlight}</TableCell>
                      <TableCell sx={{ border: 1.5 }} align="center">{hhmmss(fn.lastUpdated)}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ border: 1.5 }} style={{ padding: 0 }} colSpan={isArrivals ? 14 : 14}>
                        <Collapse in={open[rowKey]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, color: lightBlue[800] }}>
                            <Typography variant="h6">Passengers on {fn.flightNumber}</Typography>

                              <Table size="small" sx={{ border: 0.5 }} aria-label="passengers">
                                <TableHead sx={{ border: 0.5, bgcolor: lightBlue["A100"] }}>
                                  <TableRow sx={{ border: 0.5 }}>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>Type</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>ID</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>First Name</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>Surname</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>School / Campus</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>UM</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>Arrival Date</TableCell>
                                    <TableCell sx={{ border: 0.5, textAlign: "center" }}>Departure Date</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {(fn.transfers || []).map((sd, idx) => {
                                    const p = getPassengerRow(sd, idx);
                                      return (
                                        <TableRow key={`${p.personType}-${p.id}`} sx={{ minWidth: 800, bgcolor: lightBlue[50] }}>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.personType}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.id}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.firstName}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.surname}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.schoolOrCampus}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.umVal}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.arrivalDate}</TableCell>
                                          <TableCell sx={{ border: 0.5, textAlign: "center" }}>{p.departureDate}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                </TableBody>
                              </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
